import { useState } from 'react';
import { Modal } from './Shared';
import Icon from './Icon';

export const UNCLAIMED = '__unclaimed__';

/**
 * Records one goal: who scored it, and who set it up.
 *
 * Modelling a goal as a single event is what stops the two counters
 * contradicting each other — an assist cannot exist without the goal it
 * assisted, because you can only reach this screen by adding a goal.
 */
export default function GoalModal({ teamLabel, players, onAdd, onCancel }) {
  const [scorerId, setScorerId] = useState(null);
  const [assistId, setAssistId] = useState(null);

  const canAdd = scorerId !== null;
  const assistCandidates = players.filter((p) => p.id !== scorerId);

  const add = () => {
    if (!canAdd) return;
    onAdd({
      scorerId: scorerId === UNCLAIMED ? null : scorerId,
      assistId: assistId === UNCLAIMED || assistId === null ? null : assistId,
    });
  };

  return (
    <Modal
      title={`Goal for ${teamLabel}`}
      onClose={onCancel}
      wide
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={add} disabled={!canAdd}>
            Add goal
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <span className="field__label">Who scored?</span>
          <div className="pick">
            {players.map((p) => (
              <button
                type="button"
                key={p.id}
                className={`pick__chip ${scorerId === p.id ? 'pick__chip--on' : ''}`}
                onClick={() => {
                  setScorerId(p.id);
                  if (assistId === p.id) setAssistId(null);
                }}
              >
                {p.name}
              </button>
            ))}
            <button
              type="button"
              className={`pick__chip pick__chip--muted ${scorerId === UNCLAIMED ? 'pick__chip--on' : ''}`}
              onClick={() => setScorerId(UNCLAIMED)}
            >
              Own goal / nobody
            </button>
          </div>
        </div>

        <div className="field">
          <span className="field__label">
            Who assisted? <em className="field__hint">optional</em>
          </span>
          <div className="pick">
            {assistCandidates.map((p) => (
              <button
                type="button"
                key={p.id}
                className={`pick__chip ${assistId === p.id ? 'pick__chip--on' : ''}`}
                onClick={() => setAssistId(assistId === p.id ? null : p.id)}
                disabled={!canAdd}
              >
                {p.name}
              </button>
            ))}
            <button
              type="button"
              className={`pick__chip pick__chip--muted ${assistId === null ? 'pick__chip--on' : ''}`}
              onClick={() => setAssistId(null)}
              disabled={!canAdd}
            >
              No assist
            </button>
          </div>
          {!canAdd && <p className="field__note">Pick a scorer first.</p>}
        </div>

        {canAdd && (
          <p className="goal-preview">
            <Icon name="check" size={14} />
            {scorerId === UNCLAIMED ? 'Unclaimed goal' : players.find((p) => p.id === scorerId)?.name}
            {assistId ? ` — assisted by ${players.find((p) => p.id === assistId)?.name}` : ''}
          </p>
        )}
      </div>
    </Modal>
  );
}
